import { useQueryClient } from '@tanstack/react-query'
import { Feather, ImagePlus, NotebookPen, ScanText, Tag } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { Action } from '@/components/ActionMenu'
import { UploadTray } from '@/components/AddPhotos'
import { CreateAction } from '@/components/CreateAction'
import { ShareWith } from '@/components/ShareWith'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { addForLabels } from '@/lib/addFor'
import { useAddPhotos } from '@/lib/addPhotos'
import { PHOTO_TYPES } from '@/lib/photos'
import type { Person } from '@/lib/tree'
import { useMe } from '@/lib/useMe'
import { useSections } from '@/lib/sections'
import { ONLY_ME, useLastShare, type Audience } from '@/lib/spaces'
import { posts } from '../../shared/uses'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// Adding to someone's page in one step: photos of them, a moment with them, a recipe of theirs. Whatever is added from
// here says who it's about, so it lands on this page. Photos are tagged with them as they upload (said before they
// go), and go in no album, kept for you unless you share them; a moment starts with them tagged and a recipe with
// them as whose it is, both still yours to change before saving. A tag says who's in it and never who sees it. On a
// phone it's the "+" in the top bar; on a bigger screen a button under their name.
export function AddFor({ person }: { person: Person }) {
  const sections = useSections()
  const uses = useMe().data?.uses
  // Only the sections you add to yourself: none where you've picked Just see.
  const adds = (s: 'photos' | 'moments' | 'recipes') => !!sections?.[s] && posts(uses, s)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [picker, setPicker] = useState<HTMLInputElement | null>(null)
  const [picked, setPicked] = useState<File[] | null>(null)
  const adding = useAddPhotos({ familyId: person.familyId, spaceIds: [], people: [person.id] }, person.familyId, () => {
    void queryClient.invalidateQueries({ queryKey: ['person'] })
  })
  const says = addForLabels(person)
  const recipe = (write: boolean) => {
    const query = new URLSearchParams({ from: person.id })
    if (write) query.set('write', '1')
    navigate(`/circle/${person.familyId}/recipes/new?${query}`)
  }
  const actions: Action[] = [
    ...(adds('photos') ? [{ label: says.photos, icon: ImagePlus, onSelect: () => picker?.click() }] : []),
    ...(adds('moments')
      ? [
          {
            label: says.moment,
            icon: Feather,
            onSelect: () =>
              navigate(`/circle/${person.familyId}/moments/new?${new URLSearchParams({ with: person.id })}`),
          },
        ]
      : []),
    // A pet has no recipes of its own.
    ...(adds('recipes') && !person.pet
      ? [
          { label: says.scan, icon: ScanText, onSelect: () => recipe(false) },
          { label: says.write, icon: NotebookPen, onSelect: () => recipe(true) },
        ]
      : []),
  ]
  if (actions.length === 0) return null

  return (
    <>
      <input
        ref={setPicker}
        type="file"
        accept={PHOTO_TYPES}
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid="add-for-photos"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])]
          e.target.value = ''
          if (files.length) setPicked(files)
        }}
      />
      <CreateAction title={says.menu} actions={actions} />
      {picked && (
        <TagAndShare
          person={person}
          n={picked.length}
          onCancel={() => setPicked(null)}
          onChoose={(who) => {
            adding.add(picked, { familyId: person.familyId, ...who, people: [person.id] })
            setPicked(null)
          }}
        />
      )}
      {adding.pending > 0 && (
        <div className="w-full text-left">
          <UploadTray adding={adding} />
        </div>
      )}
    </>
  )
}

// Before the photos go: who they'll be tagged with, and who sees them.
function TagAndShare({
  person,
  n,
  onCancel,
  onChoose,
}: {
  person: Person
  n: number
  onCancel: () => void
  onChoose: (who: Audience) => void
}) {
  const lastShare = useLastShare()
  const [who, setWho] = useState<Audience>(() => lastShare.start(ONLY_ME))
  const tagged = addForLabels(person).tagged
  const them = n === 1 ? 'it' : 'them'
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault()
            lastShare.remember(who)
            onChoose(who)
          }}
        >
          <DialogHeader>
            <DialogTitle>{n === 1 ? `Add a photo of ${tagged}?` : `Add ${n} photos of ${tagged}?`}</DialogTitle>
            <DialogDescription>
              {n === 1 ? 'It' : `All ${n}`} will be tagged {tagged}, so {them} show{n === 1 ? 's' : ''} on{' '}
              {person.self ? 'your' : 'this'} page. Anyone else in {them} can be tagged afterwards, and a tag can be
              taken off any photo.
            </DialogDescription>
          </DialogHeader>
          <p className="flex items-center gap-2 text-sm font-medium">
            <Tag className="size-4" aria-hidden /> Tagged: {tagged}
          </p>
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium">Who sees {them}?</p>
            <ShareWith
              here={person.familyId}
              value={who}
              onChange={setWho}
              onlyMeHint={`Just you, until you share ${them}`}
            />
          </div>
          <Button type="submit" size="lg" className="self-start">
            <ImagePlus aria-hidden /> Add {plural(n, 'photo')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
